import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { http } from '../../lib/http';
import { Badge } from '../../components/ui/badge';
import { clearPricingCache } from '../../lib/pricing';
import { todayToronto, daysAgoToronto, monthsAgoToronto, startOfCurrentMonthToronto } from '../../lib/time';
import {
  DollarSign, Users, TrendingUp, TrendingDown, PieChart,
  Calendar, Plus, Trash2, LogOut, BarChart3, Award,
  Users as UsersIcon, Truck, Utensils, RefreshCw, Layers,
  Clock, Bell, UserCog, ScrollText, FileText, CheckCircle
} from 'lucide-react';

// Import Admin Components for Full Visibility
import CustomersTab from '../../components/admin/CustomersTab';
import RenewalsTab from '../../components/admin/RenewalsTab';
import DispatchingTab from '../../components/admin/DispatchingTab';
import DriverManagementTab from '../../components/admin/DriverManagementTab';
import WeeklyMenuTab from '../../components/admin/WeeklyMenuTab';
import ActivityLogTab from '../../components/admin/ActivityLogTab';
import PaymentHistoryTab from '../../components/owner/PaymentHistoryTab';
import FeedbackTab from '../../components/owner/FeedbackTab';
import CancellationRequestsTab from '../../components/shared/CancellationRequestsTab';
import CustomerPasswordsTab from '../../components/admin/CustomerPasswordsTab';

const OwnerDashboardNew = ({ onSwitchDashboard }) => {
  const { user, logout } = useAuth();

  // Strict Access Control: Only 'owner' role can view this dashboard
  // Admins are explicitly blocked even if they manage to route here
  if (!user || user.role !== 'owner') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center p-8 bg-gray-900 rounded-xl border border-red-500 shadow-2xl">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">Restricted Area: Owners Only</p>
          <button
            onClick={() => onSwitchDashboard ? onSwitchDashboard(user?.role || 'login') : window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Tab state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [activeOperationsTab, setActiveOperationsTab] = useState('routes'); // routes, drivers, kitchen
  const [cancellationCount, setCancellationCount] = useState(0);

  // Time range state
  const [timeRange, setTimeRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);
  // Audit FIX: Separate loading flags to prevent race condition between parallel API calls
  const [financialLoading, setFinancialLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [personalExpensesLoading, setPersonalExpensesLoading] = useState(false);
  const loading = financialLoading || metricsLoading || personalExpensesLoading;

  // Financial data
  const [financial, setFinancial] = useState({
    total_income: 0,
    total_business_expenses: 0,
    total_personal_expenses: 0,
    business_profit: 0,
    net_profit_after_personal: 0,
    business_expense_breakdown: {},
    personal_expense_breakdown: {}
  });

  // Business metrics
  const [businessMetrics, setBusinessMetrics] = useState({
    total_active_subscribers: 0,
    daily_new_signups: 0,
    daily_cancelled: 0,
    daily_renewed: 0,
    avg_revenue_per_customer: 0,
    top_plans: []
  });

  // Personal expenses
  const [personalExpenses, setPersonalExpenses] = useState([]);
  const [expenseReasons, setExpenseReasons] = useState([]);
  const [newExpense, setNewExpense] = useState({
    amount: '',
    expense_date: todayToronto(), // Issue #3 FIX: Use Toronto timezone
    reason: '',
    description: '',
    payment_method: ''
  });
  const [newReasonInput, setNewReasonInput] = useState('');
  const [showNewReasonInput, setShowNewReasonInput] = useState(false);
  const [showManageReasonsModal, setShowManageReasonsModal] = useState(false);
  const [personalExpensesRange, setPersonalExpensesRange] = useState('week'); // 'week' | 'month' | 'all' | 'custom_month'
  const [personalExpenseMonthFilter, setPersonalExpenseMonthFilter] = useState(''); // 'YYYY-MM'
  const [personalExpensesTotalAmount, setPersonalExpensesTotalAmount] = useState(0);

  // Income History state (view-only for owner)
  const [incomeTransactions, setIncomeTransactions] = useState([]);
  const [isIncomeLoading, setIsIncomeLoading] = useState(false);
  const [incomeFilterDate, setIncomeFilterDate] = useState('');
  const [incomeMonthFilter, setIncomeMonthFilter] = useState(''); // 'YYYY-MM' or ''
  const [financialSummary, setFinancialSummary] = useState({
    todayIncome: 0,
    todayBusinessExpenses: 0,
    todayPersonalExpenses: 0,
    todayProfit: 0,
    totalIncome: 0,
    totalBusinessExpenses: 0,
    totalPersonalExpenses: 0,
    totalProfit: 0
  });
  const [summaryDateRange, setSummaryDateRange] = useState('today'); // today, week, month

  // Change Password state
  const [dashboardUsers, setDashboardUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Change Pricing state
  // const [pricing, setPricing] = useState({ plans: {}, truckers: {}, addons: {} });
  const [editingPricing, setEditingPricing] = useState({ plans: {}, truckers: {}, addons: {} });

  // User Management state
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    full_name: '',
    role: 'admin',
    password: 'login',
    assigned_route_id: ''
  });

  // Phase 11: Owner Ultimate Control - Modal States
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: '',
    reason: '',
    reauthPassword: ''
  });
  const [changeRoleForm, setChangeRoleForm] = useState({
    newRole: '',
    reason: '',
    reauthPassword: ''
  });
  const [deleteDisableForm, setDeleteDisableForm] = useState({
    reason: '',
    confirmAction: false,
    reauthPassword: ''
  });

  // Change Own Password state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });



  // Staff Time Logs State
  const [staffLogs, setStaffLogs] = useState([]);
  const [logFilterDate, setLogFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [logFilterRole, setLogFilterRole] = useState('kitchen');

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Audit FIX #1: Double-submit protection flag for all destructive actions
  const [submitting, setSubmitting] = useState(false);

  // Audit FIX #10: Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Clear state on component mount
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      setError('');
      setMessage('');
      setUsers([]);
      setRoutes([]);
    };
  }, []);

  // Load cancellation requests count
  const loadCancellationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await http.get('/owner/cancellation-requests', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.requests) {
        const pending = response.data.requests.filter(r => r.status === 'pending_attention').length;
        setCancellationCount(pending);
      }
    } catch (error) {
      console.error('Failed to load cancellation count:', error);
    }
  };

  useEffect(() => {
    loadCancellationCount();
    const cancellationInterval = setInterval(loadCancellationCount, 60000);
    return () => clearInterval(cancellationInterval);
  }, []);

  useEffect(() => {
    // Clear errors and messages when changing tabs
    setError('');
    setMessage('');

    // Audit FIX #3: Skip fetch when custom range is selected but dates aren't set yet
    if (timeRange === 'custom' && (!customStartDate || !customEndDate)) {
      return;
    }

    if (activeTab === 'dashboard') {
      // Batch independent API calls in parallel for better performance
      Promise.all([
        loadFinancialOverview(),
        loadBusinessMetrics(),
        loadPersonalExpenses(),
        loadExpenseReasons()
      ]);
    } else if (activeTab === 'users') {
      loadUsers();
      loadRoutes();
    } else if (activeTab === 'pricing') {
      loadPricing();

    } else if (activeTab === 'expenses') {
      loadPersonalExpenses();
      loadExpenseReasons();
    } else if (activeTab === 'staff-login') {
      loadStaffLogs();
    }
    // New tabs (customers, operations, renewals) load their own data internally

  }, [timeRange, activeTab]);

  // Audit FIX: Use useEffect to reload income transactions when filter date changes
  // This replaces the fragile setTimeout(loadIncomeTransactions, 100) pattern
  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'income') {
      loadIncomeTransactions();
    }
    if (activeTab === 'income') {
      loadFinancialSummary('today');
    }
  }, [incomeFilterDate, incomeMonthFilter, activeTab]);

  // Audit FIX: Memoize expense breakdown computation to avoid re-calculating on every render
  const expenseBreakdownByReason = useMemo(() => {
    const byReason = {};
    personalExpenses.forEach(e => {
      const reason = e.reason || 'Unknown';
      if (!byReason[reason]) {
        byReason[reason] = { count: 0, total: 0 };
      }
      byReason[reason].count++;
      byReason[reason].total += parseFloat(e.amount || 0);
    });
    return Object.entries(byReason).sort((a, b) => b[1].total - a[1].total);
  }, [personalExpenses]);

  const loadFinancialOverview = async () => {
    try {
      setFinancialLoading(true);
      let url = `/owner/dashboard/financial-overview?time_range=${timeRange}`;

      // Add custom date range if selected
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        url = `/owner/dashboard/financial-overview?time_range=custom&start_date=${customStartDate}&end_date=${customEndDate}`;
      }

      const response = await http.get(url);
      if (response.data.success) {
        // Single atomic state update to prevent race condition
        setFinancial({
          ...response.data.data.metrics,
          business_expense_breakdown: response.data.data.metrics.business_expense_breakdown || {},
          personal_expense_breakdown: response.data.data.metrics.personal_expense_breakdown || {}
        });

        // Audit FIX: Map summary data from the SAME response instead of making a duplicate API call
        const { today, range: rangeData } = response.data.data;
        if (today && rangeData) {
          setFinancialSummary({
            todayIncome: today.income,
            todayBusinessExpenses: today.business_expenses,
            todayPersonalExpenses: today.personal_expenses,
            todayProfit: today.profit,
            totalIncome: rangeData.total_income,
            totalBusinessExpenses: rangeData.total_business_expenses,
            totalPersonalExpenses: rangeData.total_personal_expenses,
            totalProfit: rangeData.net_profit_after_personal
          });
        }
      }
    } catch (error) {
      console.error('Failed to load financial overview:', error);
      setError('Failed to load financial data');
    } finally {
      setFinancialLoading(false);
    }
    // Audit FIX #9: Removed unnecessary loadIncomeTransactions() call here.
    // Income is loaded independently via the incomeFilterDate useEffect.
  };

  const loadBusinessMetrics = async () => {
    try {
      setMetricsLoading(true);
      let url = `/owner/dashboard/business-metrics?time_range=${timeRange}`;

      // Add custom date range if selected
      if (timeRange === 'custom' && customStartDate && customEndDate) {
        url = `/owner/dashboard/business-metrics?time_range=custom&start_date=${customStartDate}&end_date=${customEndDate}`;
      }

      const response = await http.get(url);
      if (response.data.success) {
        // Single atomic state update to prevent race condition
        setBusinessMetrics({
          ...response.data.data?.metrics,
          top_plans: response.data.data?.top_plans || []
        });
      }
    } catch (error) {
      console.error('Failed to load business metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadPersonalExpenses = async (startDate = null, endDate = null) => {
    try {
      setPersonalExpensesLoading(true);
      const params = { limit: 1000 };

      // If dates aren't passed, try to use the current range state
      let rangeStart = startDate;
      let rangeEnd = endDate;

      if (!startDate && !endDate) {
        if (personalExpensesRange === 'week') {
          rangeStart = daysAgoToronto(7);
          rangeEnd = todayToronto();
        } else if (personalExpensesRange === 'month') {
          rangeStart = startOfCurrentMonthToronto();
          rangeEnd = todayToronto();
        } else if (personalExpensesRange === 'custom_month' && personalExpenseMonthFilter) {
          const [year, month] = personalExpenseMonthFilter.split('-');
          const lastDay = new Date(year, month, 0).getDate();
          rangeStart = `${personalExpenseMonthFilter}-01`;
          rangeEnd = `${personalExpenseMonthFilter}-${lastDay}`;
        }
      }

      if (rangeStart) params.start_date = rangeStart;
      if (rangeEnd) params.end_date = rangeEnd;

      const response = await http.get('/owner/personal-expenses', { params });
      if (response.data.success) {
        setPersonalExpenses(response.data.data.expenses || []);
        if (response.data.data.total_amount !== undefined) {
          setPersonalExpensesTotalAmount(response.data.data.total_amount);
        } else {
          setPersonalExpensesTotalAmount((response.data.data.expenses || []).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0));
        }
      }
    } catch (error) {
      console.error('Failed to load personal expenses:', error);
    } finally {
      setPersonalExpensesLoading(false);
    }
  };

  const loadExpenseReasons = async () => {
    try {
      setPersonalExpensesLoading(true);
      const response = await http.get('/owner/personal-expense-reasons');
      if (response.data.success) {
        setExpenseReasons(response.data.data.reasons || []);
      }
    } catch (error) {
      console.error('Failed to load expense reasons:', error);
    } finally {
      setPersonalExpensesLoading(false);
    }
  };

  const addPersonalExpense = async () => {
    if (submitting) return; // Audit FIX #1: Prevent double-submit
    try {
      if (!newExpense.amount || !newExpense.reason) {
        setError('Amount and reason are required');
        return;
      }
      setSubmitting(true);

      const response = await http.post('/owner/personal-expenses', newExpense);
      if (response.data.success) {
        setMessage('Personal expense added successfully');
        setNewExpense({
          amount: '',
          expense_date: todayToronto(), // Issue #3 FIX: Use Toronto timezone
          reason: '',
          description: '',
          payment_method: ''
        });
        setShowNewReasonInput(false);
        setNewReasonInput('');
        loadPersonalExpenses();
        loadFinancialOverview();
      }
    } catch (error) {
      console.error('Failed to add expense:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const deletePersonalExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      const response = await http.delete(`/owner/personal-expenses/${expenseId}`);
      if (response.data.success) {
        setMessage('Expense deleted successfully');
        loadPersonalExpenses();
        loadFinancialOverview();
      }
    } catch (error) {
      console.error('Failed to delete expense:', error);
      setError('Failed to delete expense');
    }
  };

  const addExpenseReason = async () => {
    try {
      if (!newReasonInput.trim()) {
        setError('Reason name is required');
        return;
      }

      const response = await http.post('/owner/personal-expense-reasons', { reason: newReasonInput });
      if (response.data.success) {
        setMessage('Expense reason added successfully');
        // Optimistically add the new reason to state so it shows immediately in the dropdown
        const addedReason = newReasonInput.trim();
        setExpenseReasons(prev => [...prev, addedReason].sort());
        // Set the newly added reason as selected
        setNewExpense({ ...newExpense, reason: addedReason });
        setNewReasonInput('');
        setShowNewReasonInput(false);
        loadExpenseReasons(); // sync from server
      }
    } catch (error) {
      console.error('Failed to add reason:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to add reason');
    }
  };

  const deleteExpenseReasonHandler = async (reasonText) => {
    if (!window.confirm(`Are you sure you want to delete the reason "${reasonText}"?`)) return;

    try {
      const response = await http.delete(`/owner/personal-expense-reasons/${encodeURIComponent(reasonText)}`);
      if (response.data.success) {
        setMessage('Expense reason deleted successfully');

        // If the deleted reason was currently selected in the form, clear it
        if (newExpense.reason === reasonText) {
          setNewExpense({ ...newExpense, reason: '' });
        }

        loadExpenseReasons();
      }
    } catch (error) {
      console.error('Failed to delete reason:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to delete reason');
    }
  };

  // Load income transactions and financial summary
  const loadIncomeTransactions = async () => {
    try {
      setIsIncomeLoading(true);
      const params = { limit: 1000 };
      if (incomeFilterDate) {
        params.start_date = incomeFilterDate;
        params.end_date = incomeFilterDate;
      } else if (incomeMonthFilter) {
        const [year, month] = incomeMonthFilter.split('-');
        const lastDay = new Date(year, month, 0).getDate();
        params.start_date = `${incomeMonthFilter}-01`;
        params.end_date = `${incomeMonthFilter}-${lastDay}`;
      }

      const response = await http.get('/admin/income/transactions', { params });
      if (response.data.success) {
        setIncomeTransactions(response.data.data?.transactions || []);
      }
    } catch (error) {
      console.error('Failed to load income transactions:', error);
      setError('Failed to load income history');
    } finally {
      setIsIncomeLoading(false);
    }
  };

  // Load Staff Time Logs
  const loadStaffLogs = async () => {
    try {
      const response = await http.get('/owner/staff-logs', {
        params: {
          date: logFilterDate,
          role: logFilterRole
        }
      });

      if (response?.data?.success) {
        setStaffLogs(response?.data?.data?.logs || []);
      } else {
        setStaffLogs([]);
      }
    } catch (error) {
      console.error('Failed to load staff logs:', error);
      setError('Failed to load staff time logs');
      setStaffLogs([]);
    }
  };

  // Reload logs when filters change
  useEffect(() => {
    if (activeTab === 'staff-login') {
      loadStaffLogs();
    }
  }, [activeTab, logFilterDate, logFilterRole]);

  // Delete income transaction
  const handleDeleteIncome = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this income entry? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await http.delete(`/admin/income/transactions/${transactionId}`);

      if (response.data.success) {
        setMessage('Income entry deleted successfully');
        // Reload transactions and financial overview (which includes summary)
        await loadIncomeTransactions();
        await loadFinancialOverview();
      } else {
        setError('Failed to delete income entry');
      }
    } catch (error) {
      console.error('Failed to delete income:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to delete income entry');
    }
  };


  // Load financial summary for today/range
  // Issue #4 FIX: Use the single getFinancialOverview endpoint instead of 6 parallel API calls
  // This endpoint handles Toronto timezone correctly and returns both today AND range values
  const loadFinancialSummary = async (range = 'today') => {
    try {
      // Single API call to get all financial data (replaces 6 separate calls)
      const response = await http.get('/owner/dashboard/financial-overview', {
        params: { time_range: range }
      });

      if (response.data.success) {
        const { today, range: rangeData } = response.data.data;

        setFinancialSummary({
          todayIncome: today.income,
          todayBusinessExpenses: today.business_expenses,
          todayPersonalExpenses: today.personal_expenses,
          todayProfit: today.profit,
          totalIncome: rangeData.total_income,
          totalBusinessExpenses: rangeData.total_business_expenses,
          totalPersonalExpenses: rangeData.total_personal_expenses,
          totalProfit: rangeData.net_profit_after_personal
        });
      }
    } catch (error) {
      console.error('Failed to load financial summary:', error);
    }
  };

  // Export income CSV for owner
  const exportIncomeCSV = () => {
    if (incomeTransactions.length === 0) {
      setError('No income data to export');
      return;
    }

    // Helper to escape CSV values containing commas, quotes, or newlines
    const escapeCSV = (value) => {
      const str = String(value || '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = ['Date', 'First Name', 'Last Name', 'Plan Key', 'Add-ons', 'Total Amount'];
    const rows = incomeTransactions.map(t => {
      const nameParts = (t.customer_name || '').split(' ');
      const firstName = nameParts[0] || '-';
      const lastName = nameParts.slice(1).join(' ') || '-';

      const localDate = t.payment_date ? new Date(t.payment_date).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }) : '-';

      return [
        escapeCSV(localDate),
        escapeCSV(firstName),
        escapeCSV(lastName),
        escapeCSV(t.plan_key || '-'),
        escapeCSV(t.add_ons || '-'),
        escapeCSV(`$${t.total_amount}`)
      ];
    });

    const csv = [headers.map(escapeCSV), ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `income_${todayToronto()}.csv`; // Issue #3 FIX: Use Toronto timezone
    a.click();
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };


  // User Management functions
  const loadUsers = async () => {
    try {
      const response = await http.get('/admin/users');
      if (response.data.success) {
        // Filter out any user that holds the "customer" role.
        const filteredUsers = (response.data.data || []).filter(user => user.role !== 'customer');
        setUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    }
  };

  const loadRoutes = async () => {
    try {
      const response = await http.get('/admin/routes');
      if (response.data.success) {
        setRoutes(response.data.routes || []);
      }
    } catch (error) {
      console.error('Failed to load routes:', error);
    }
  };


  const handleCreateUser = () => {
    // Clear any previous errors/messages
    setError('');
    setMessage('');
    setEditingUser(null);
    setUserForm({
      email: '',
      full_name: '',
      role: 'admin',
      password: '',
      assigned_route_id: ''
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    // Clear any previous errors/messages
    setError('');
    setMessage('');
    setEditingUser(user);
    setUserForm({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      password: '',
      assigned_route_id: user.assigned_route_id || ''
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      // Clear previous errors
      setError('');
      setMessage('');

      if (!userForm.email || !userForm.full_name) {
        setError('Please fill required fields');
        return;
      }
      setSubmitting(true);

      if (editingUser) {
        const response = await http.put(`/admin/users/${editingUser.id}`, {
          full_name: userForm.full_name,
          role: userForm.role,
          // Only send password if it's not empty
          ...(userForm.password ? { password: userForm.password } : {}),
          assigned_route_id: userForm.role === 'driver' ? userForm.assigned_route_id : undefined
        });

        if (response.data.success) {
          setMessage('User updated successfully');
          setShowUserModal(false);
          setEditingUser(null);
          await loadUsers();
        }
      } else {
        const response = await http.post('/admin/users', {
          email: userForm.email,
          full_name: userForm.full_name,
          role: userForm.role,
          password: userForm.password,
          assigned_route_id: userForm.role === 'driver' ? userForm.assigned_route_id : undefined
        });

        if (response.data.success) {
          setMessage('User created successfully');
          setShowUserModal(false);
          setEditingUser(null);
          await loadUsers();
        }
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 11: Reset Password Handler
  const handleResetPassword = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      if (!resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword) {
        setError('Please fill all password fields');
        return;
      }

      if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (!resetPasswordForm.reason || resetPasswordForm.reason.trim().length < 10) {
        setError('Reason is required and must be at least 10 characters');
        return;
      }

      if (!resetPasswordForm.reauthPassword) {
        setError('Password re-authentication is required');
        return;
      }
      setSubmitting(true);

      const response = await http.post(`/admin/users/${selectedUserForAction.id}/reset-password`, {
        newPassword: resetPasswordForm.newPassword,
        reason: resetPasswordForm.reason.trim(),
        password: resetPasswordForm.reauthPassword
      });

      setMessage(response.data.message || 'Password reset successfully');
      setShowResetPasswordModal(false);
      setResetPasswordForm({ newPassword: '', confirmPassword: '', reason: '', reauthPassword: '' });
      setSelectedUserForAction(null);
      await loadUsers();
    } catch (error) {
      console.error('Failed to reset password:', error);
      setError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 11: Change Role Handler
  const handleChangeRole = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      if (!changeRoleForm.newRole) {
        setError('Please select a new role');
        return;
      }

      if (!changeRoleForm.reason || changeRoleForm.reason.trim().length < 10) {
        setError('Reason is required and must be at least 10 characters');
        return;
      }

      if (!changeRoleForm.reauthPassword) {
        setError('Password re-authentication is required');
        return;
      }
      setSubmitting(true);

      const response = await http.put(`/admin/users/${selectedUserForAction.id}/permissions`, {
        newRole: changeRoleForm.newRole,
        reason: changeRoleForm.reason.trim(),
        password: changeRoleForm.reauthPassword
      });

      setMessage(response.data.message || 'User role updated successfully');
      setShowChangeRoleModal(false);
      setChangeRoleForm({ newRole: '', reason: '', reauthPassword: '' });
      setSelectedUserForAction(null);
      await loadUsers();
    } catch (error) {
      console.error('Failed to change role:', error);
      setError(error.response?.data?.message || 'Failed to change role');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 11: Delete User Handler (with reason)
  const handleDeleteUser = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      if (!deleteDisableForm.reason || deleteDisableForm.reason.trim().length < 10) {
        setError('Reason is required and must be at least 10 characters');
        return;
      }

      if (!deleteDisableForm.confirmAction) {
        setError('Please confirm this action by checking the checkbox');
        return;
      }

      if (!deleteDisableForm.reauthPassword) {
        setError('Password re-authentication is required');
        return;
      }
      setSubmitting(true);

      const response = await http.delete(`/admin/users/${selectedUserForAction.id}`, {
        data: {
          reason: deleteDisableForm.reason.trim(),
          password: deleteDisableForm.reauthPassword
        }
      });

      setMessage(response.data.message || 'User deactivated successfully');
      setShowDeleteModal(false);
      setDeleteDisableForm({ reason: '', confirmAction: false, reauthPassword: '' });
      setSelectedUserForAction(null);
      await loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 11: Disable User Handler (with reason)
  const handleDisableUser = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      if (!deleteDisableForm.reason || deleteDisableForm.reason.trim().length < 10) {
        setError('Reason is required and must be at least 10 characters');
        return;
      }

      if (!deleteDisableForm.confirmAction) {
        setError('Please confirm this action by checking the checkbox');
        return;
      }

      if (!deleteDisableForm.reauthPassword) {
        setError('Password re-authentication is required');
        return;
      }
      setSubmitting(true);

      const response = await http.post(`/admin/users/${selectedUserForAction.id}/revoke-access`, {
        reason: deleteDisableForm.reason.trim(),
        password: deleteDisableForm.reauthPassword
      });

      setMessage(response.data.message || 'User access revoked successfully');
      setShowDisableModal(false);
      setDeleteDisableForm({ reason: '', confirmAction: false, reauthPassword: '' });
      setSelectedUserForAction(null);
      await loadUsers();
    } catch (error) {
      console.error('Failed to disable user:', error);
      setError(error.response?.data?.message || 'Failed to disable user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeOwnPassword = async () => {
    try {
      if (!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password) {
        setError('Please fill all fields');
        return;
      }

      if (passwordForm.new_password !== passwordForm.confirm_password) {
        setError('New passwords do not match');
        return;
      }

      if (passwordForm.new_password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      const response = await http.put('/owner/change-own-password', {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      });

      if (response.data.success) {
        setMessage('Your password has been changed successfully');
        setShowChangePasswordModal(false);
        setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to change password');
    }
  };

  // Change Password functions
  const loadDashboardUsers = async () => {
    try {
      const response = await http.get('/owner/dashboard-users');
      if (response.data.success) {
        setDashboardUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard users:', error);
      setError('Failed to load users');
    }
  };

  const changePassword = async () => {
    try {
      if (!selectedUser || !newPassword || !confirmPassword) {
        setError('Please fill all fields');
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }

      const response = await http.post('/owner/change-password', {
        user_id: selectedUser,
        new_password: newPassword
      });

      if (response.data.success) {
        setMessage('Password changed successfully');
        setSelectedUser('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to change password');
    }
  };

  // Change Pricing functions
  const loadPricing = async () => {
    try {
      const response = await http.get("/owner/pricing");

      if (response.data.success) {
        const pricingData = response.data?.data?.data?.pricing || {
          plans: {},
          truckers: {},
          addons: {},
        };

        setEditingPricing(pricingData);
        console.log("pricingData", pricingData);
      }
    } catch (error) {
      console.error("Failed to load pricing:", error);
      setError("Failed to load pricing");
    }
  };

  const updatePricing = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      setSubmitting(true);
      const response = await http.post('/owner/pricing/update', {
        plans: editingPricing.plans,
        truckers: editingPricing.truckers,
        addons: editingPricing.addons,
        effective_date: todayToronto() // Issue #3 FIX: Use Toronto timezone
      });

      if (response.data.success) {
        // Clear pricing cache so components fetch new pricing
        clearPricingCache();

        setMessage('Pricing updated successfully! New prices will apply to all new customers and renewals.');
        loadPricing();
      }
    } catch (error) {
      console.error('Failed to update pricing:', error);
      setError(error.response?.data?.detail || error.response?.data?.message || 'Failed to update pricing');
    } finally {
      setSubmitting(false);
    }
  };




  // Settings functions
  const loadSettings = async () => {
    try {
      const response = await http.get('/owner/settings');
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      setError('Failed to load settings');
    }
  };

  const updateSystemSettings = async () => {
    if (submitting) return; // Audit FIX #1
    try {
      setSubmitting(true);
      const response = await http.put('/owner/settings', settings);
      if (response.data.success) {
        setMessage('Settings updated successfully');
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      setError('Failed to update settings');
    } finally {
      setSubmitting(false);
    }
  };

  const updatePlanPrice = (mealType, mealTerm, billingTerm, newPrice) => {
    // Audit FIX: Deep-clone nested objects to avoid mutating state reference
    const updated = JSON.parse(JSON.stringify(editingPricing));
    if (!updated.plans[mealType]) updated.plans[mealType] = {};
    if (!updated.plans[mealType][mealTerm]) updated.plans[mealType][mealTerm] = {};
    updated.plans[mealType][mealTerm][billingTerm] = parseFloat(newPrice);
    setEditingPricing(updated);
  };

  const updateAddonPrice = (addonName, billingTerm, newPrice) => {
    const updated = JSON.parse(JSON.stringify(editingPricing));
    if (!updated.addons[addonName]) updated.addons[addonName] = {};
    updated.addons[addonName][billingTerm] = parseFloat(newPrice);
    setEditingPricing(updated);
  };

  const updateTruckerPrice = (truckerType, truckerTerm, newPrice) => {
    const updated = JSON.parse(JSON.stringify(editingPricing));
    if (!updated.truckers[truckerType]) updated.truckers[truckerType] = {};
    updated.truckers[truckerType][truckerTerm] = parseFloat(newPrice);
    setEditingPricing(updated);
  };

  const timeRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: '6months', label: '6 Months' },
    { value: 'year', label: '1 Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const applyCustomRange = () => {
    if (!customStartDate || !customEndDate) {
      setError('Please select both start and end dates');
      return;
    }
    loadFinancialOverview();
    loadBusinessMetrics();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-black border-b-2 border-orange-500 shadow-lg shadow-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img
                src="/images/just-protein-logo.png"
                alt="Just Protein"
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-orange-500 shadow-lg shadow-orange-500/50"
              />
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">Just Protein</h1>
                <p className="text-xs sm:text-sm text-gray-300">Owner Dashboard</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <Badge className="bg-orange-500 text-white text-xs">OWNER</Badge>
                <span className="text-white text-xs sm:text-sm truncate max-w-[150px]">{user?.email}</span>
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => onSwitchDashboard && onSwitchDashboard('admin')}
                  className="px-2 sm:px-4 py-1 sm:py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md border-2 border-orange-600 transition-colors text-xs sm:text-sm"
                >
                  Admin
                </button>

                {onSwitchDashboard && (
                  <button
                    onClick={() => onSwitchDashboard('selector')}
                    className="px-2 sm:px-4 py-1 sm:py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md border-2 border-purple-700 transition-colors text-xs sm:text-sm font-bold flex items-center gap-2"
                  >
                    <UserCog size={16} />
                    <span>Switch View</span>
                  </button>
                )}
                <button
                  onClick={logout}
                  className="px-2 sm:px-4 py-1 sm:py-2 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center space-x-1 text-xs sm:text-sm"
                >
                  <LogOut size={14} className="sm:w-[18px] sm:h-[18px]" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {(message || error) && (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full px-4 sm:px-0">
          {message && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative shadow-lg animate-fade-in-down">
              <span className="block sm:inline">{message}</span>
              <button onClick={() => setMessage('')} className="absolute top-0 right-0 px-4 py-3">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-lg animate-fade-in-down">
              <span className="block sm:inline">{error}</span>
              <button onClick={() => setError('')} className="absolute top-0 right-0 px-4 py-3">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex space-x-2 border-b-2 border-gray-700 overflow-x-auto">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${activeTab === 'dashboard'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('income')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${activeTab === 'income'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            Today's Income
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${activeTab === 'expenses'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            Personal Expenses
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap ${activeTab === 'users'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('customers')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'customers'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <UsersIcon size={18} />
            Customers
          </button>
          <button
            onClick={() => setActiveTab('operations')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'operations'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <Layers size={18} />
            Operations
          </button>
          <button
            onClick={() => setActiveTab('renewals')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'renewals'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <RefreshCw size={18} />
            Renewals
          </button>
          <button
            onClick={() => setActiveTab('staff-login')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'staff-login'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <Layers size={18} />
            Staff Login
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'pricing'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <DollarSign size={18} />
            Change Pricing
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'logs'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <ScrollText size={18} />
            Activity Logs
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'payments'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <DollarSign size={18} />
            Payments
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'feedback'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <FileText size={18} />
            Feedback
          </button>
          <button
            onClick={() => setActiveTab('cancellations')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'cancellations'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <LogOut size={18} />
            Cancellation Requests
            {cancellationCount > 0 && (
              <span className="ml-1 inline-block px-2 py-0.5 text-xs font-semibold text-white bg-red-600 rounded-full">
                {cancellationCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('customer-passwords')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === 'customer-passwords'
              ? 'bg-orange-500 text-white border-t-2 border-l-2 border-r-2 border-orange-500 rounded-t-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
          >
            <UserCog size={18} />
            Customer Passwords
          </button>
        </div>
      </div>

      {/* Dashboard Tab Content */}
      {activeTab === 'dashboard' && (
        <>
          {/* Audit FIX #7: Show loading indicator on dashboard tab */}
          {loading && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
              <div className="flex items-center justify-center py-12">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mr-4"></div>
                <span className="text-gray-400 text-lg">Loading dashboard data...</span>
              </div>
            </div>
          )}

          {!loading && (
            <>
              {/* Time Range Selector */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
                <div className="bg-gray-800 p-4 rounded-lg border border-orange-500">
                  <div className="flex items-center flex-wrap gap-2">
                    <Calendar className="text-orange-500" size={20} />
                    <span className="text-white font-medium">Time Range:</span>
                    <div className="flex flex-wrap gap-2">
                      {timeRangeOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setTimeRange(option.value);
                            if (option.value === 'custom') {
                              setShowCustomRange(true);
                            } else {
                              setShowCustomRange(false);
                            }
                          }}
                          className={`px-4 py-2 rounded-md transition-colors ${timeRange === option.value
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Range Inputs */}
                  {showCustomRange && (
                    <div className="mt-4 flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <label className="text-white text-sm">Start Date:</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-white text-sm">End Date:</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                        />
                      </div>
                      <button
                        onClick={applyCustomRange}
                        className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Overview Cards */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
                <h2 className="text-2xl font-bold text-white mb-4">Financial Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Total Income - FIX: Removed redundant Total Revenue card */}
                  <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6 shadow-xl border-2 border-green-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Total Income</p>
                        <p className="text-3xl font-bold text-white mt-2">{formatCurrency(financial.total_income)}</p>
                      </div>
                      <DollarSign className="text-white opacity-50" size={48} />
                    </div>
                  </div>

                  {/* Business Expenses */}
                  <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-lg p-6 shadow-xl border-2 border-orange-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Business Expenses</p>
                        <p className="text-3xl font-bold text-white mt-2">{formatCurrency(financial.total_business_expenses)}</p>
                      </div>
                      <TrendingDown className="text-white opacity-50" size={48} />
                    </div>
                  </div>

                  {/* Personal Expenses */}
                  <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6 shadow-xl border-2 border-purple-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Personal Expenses</p>
                        <p className="text-3xl font-bold text-white mt-2">{formatCurrency(financial.total_personal_expenses)}</p>
                      </div>
                      <TrendingDown className="text-white opacity-50" size={48} />
                    </div>
                  </div>

                  {/* Business Profit */}
                  <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg p-6 shadow-xl border-2 border-teal-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-teal-100 text-sm">Business Profit</p>
                        <p className="text-sm text-teal-200">(Income - Business Expenses)</p>
                        <p className="text-3xl font-bold text-white mt-2">{formatCurrency(financial.business_profit)}</p>
                      </div>
                      <BarChart3 className="text-white opacity-50" size={48} />
                    </div>
                  </div>

                  {/* Net Profit After Personal */}
                  <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg p-6 shadow-xl border-2 border-indigo-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-100 text-sm">Net Profit (After Personal)</p>
                        <p className="text-sm text-indigo-200">(Business Profit - Personal Exp)</p>
                        <p className="text-3xl font-bold text-white mt-2">{formatCurrency(financial.net_profit_after_personal)}</p>
                      </div>
                      <Award className="text-white opacity-50" size={48} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Expense Breakdowns */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Business Expense Breakdown */}
                <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-orange-500">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <PieChart className="mr-2 text-orange-500" size={24} />
                    Business Expense Breakdown
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(financial.business_expense_breakdown || {}).length > 0 ? (
                      Object.entries(financial.business_expense_breakdown).map(([category, amount]) => (
                        <div key={category} className="flex justify-between items-center bg-gray-700 p-3 rounded-md">
                          <span className="text-gray-300">{category}</span>
                          <span className="text-white font-bold">{formatCurrency(amount)}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-center py-4">No business expenses for this period</p>
                    )}
                  </div>
                </div>

                {/* Personal Expense Breakdown */}
                <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-purple-500">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <PieChart className="mr-2 text-purple-500" size={24} />
                    Personal Expense Breakdown
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(financial.personal_expense_breakdown || {}).length > 0 ? (
                      Object.entries(financial.personal_expense_breakdown).map(([reason, amount]) => (
                        <div key={reason} className="flex justify-between items-center bg-gray-700 p-3 rounded-md">
                          <span className="text-gray-300">{reason}</span>
                          <span className="text-white font-bold">{formatCurrency(amount)}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 text-center py-4">No personal expenses for this period</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Business Metrics */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                <h2 className="text-2xl font-bold text-white mb-4">Business Metrics</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-orange-500">
                    <Users className="text-orange-500 mb-2" size={32} />
                    <p className="text-gray-400 text-sm">Active Subscribers</p>
                    <p className="text-2xl font-bold text-white mt-1">{businessMetrics.total_active_subscribers}</p>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-green-500">
                    <TrendingUp className="text-green-500 mb-2" size={32} />
                    <p className="text-gray-400 text-sm">New Signups</p>
                    <p className="text-2xl font-bold text-white mt-1">{businessMetrics.daily_new_signups}</p>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-red-500">
                    <TrendingDown className="text-red-500 mb-2" size={32} />
                    <p className="text-gray-400 text-sm">Cancelled (Period)</p>
                    <p className="text-2xl font-bold text-white mt-1">{businessMetrics.daily_cancelled}</p>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-blue-500">
                    <BarChart3 className="text-blue-500 mb-2" size={32} />
                    <p className="text-gray-400 text-sm">Renewed</p>
                    <p className="text-2xl font-bold text-white mt-1">{businessMetrics.daily_renewed}</p>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-teal-500">
                    <DollarSign className="text-teal-500 mb-2" size={32} />
                    <p className="text-gray-400 text-sm">Avg Monthly Rev / User</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatCurrency(businessMetrics.avg_revenue_per_customer)}</p>
                  </div>
                </div>
              </div>

              {/* Top Plans */}
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                <h2 className="text-2xl font-bold text-white mb-4">Top Plans</h2>
                <div className="bg-gray-800 rounded-lg shadow-xl border-2 border-orange-500 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Plan Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Subscribers</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {businessMetrics.top_plans && businessMetrics.top_plans.length > 0 ? (
                        businessMetrics.top_plans
                          .filter(plan => plan.plan_name && plan.plan_name.trim() !== '' && plan.plan_name !== 'Unknown')
                          .map((plan, index) => (
                            <tr key={index} className="hover:bg-gray-700">
                              <td className="px-6 py-4 text-sm text-white">{plan.plan_name}</td>
                              <td className="px-6 py-4 text-sm text-white">{plan.subscriber_count}</td>
                              <td className="px-6 py-4 text-sm text-white font-bold">{formatCurrency(plan.revenue)}</td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="px-6 py-4 text-center text-gray-400">No plans data available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Income History Tab - Renamed to Today's Income */}
      {
        activeTab === 'income' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            {/* Financial Summary Cards */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">💰 Today's Financial Summary</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSummaryDateRange('today');
                      loadFinancialSummary('today');
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium ${summaryDateRange === 'today' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => {
                      setSummaryDateRange('week');
                      loadFinancialSummary('week');
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium ${summaryDateRange === 'week' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    This Week
                  </button>
                  <button
                    onClick={() => {
                      setSummaryDateRange('month');
                      loadFinancialSummary('month');
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium ${summaryDateRange === 'month' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => {
                      setSummaryDateRange('6months');
                      loadFinancialSummary('6months');
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium ${summaryDateRange === '6months' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    6 Months
                  </button>
                  <button
                    onClick={() => {
                      setSummaryDateRange('year');
                      loadFinancialSummary('year');
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium ${summaryDateRange === 'year' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    This Year
                  </button>
                </div>
              </div>

              {/* Summary Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Today's Income */}
                <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-4 shadow-lg">
                  <p className="text-green-100 text-sm mb-1">Today's Income</p>
                  <p className="text-3xl font-bold text-white">${financialSummary.todayIncome.toFixed(2)}</p>
                </div>

                {/* Today's Business Expenses */}
                <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-lg p-4 shadow-lg">
                  <p className="text-red-100 text-sm mb-1">Today's Business Expenses</p>
                  <p className="text-3xl font-bold text-white">${financialSummary.todayBusinessExpenses.toFixed(2)}</p>
                </div>

                {/* Today's Personal Expenses */}
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-4 shadow-lg">
                  <p className="text-purple-100 text-sm mb-1">Today's Personal Expenses</p>
                  <p className="text-3xl font-bold text-white">${financialSummary.todayPersonalExpenses.toFixed(2)}</p>
                </div>

                {/* Today's Profit/Loss */}
                <div className={`bg-gradient-to-br ${financialSummary.todayProfit >= 0 ? 'from-blue-600 to-blue-700' : 'from-orange-600 to-orange-700'} rounded-lg p-4 shadow-lg`}>
                  <p className="text-blue-100 text-sm mb-1">
                    Today's {financialSummary.todayProfit >= 0 ? 'Profit ✅' : 'Loss ⚠️'}
                  </p>
                  <p className="text-3xl font-bold text-white">${Math.abs(financialSummary.todayProfit).toFixed(2)}</p>
                </div>
              </div>

              {/* Range Summary Cards */}
              {summaryDateRange !== 'today' && (
                <div className="bg-gray-800 rounded-lg p-4 border-2 border-gray-700 mb-6">
                  <h3 className="text-lg font-bold text-white mb-3">
                    {{ week: 'This Week', month: 'This Month', '6months': 'Last 6 Months', year: 'This Year' }[summaryDateRange]} Summary
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">Total Income</p>
                      <p className="text-xl font-bold text-green-400">${financialSummary.totalIncome.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Business Expenses</p>
                      <p className="text-xl font-bold text-red-400">${financialSummary.totalBusinessExpenses.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Personal Expenses</p>
                      <p className="text-xl font-bold text-purple-400">${financialSummary.totalPersonalExpenses.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Net Profit/Loss</p>
                      <p className={`text-xl font-bold ${financialSummary.totalProfit >= 0 ? 'text-blue-400' : 'text-orange-400'}`}>
                        ${financialSummary.totalProfit.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Income Transactions Table */}
            <div className="bg-gray-800 rounded-lg shadow-xl border-2 border-green-500 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">Income Transactions</h3>
                <button
                  onClick={exportIncomeCSV}
                  className="px-4 py-2 bg-white hover:bg-gray-100 text-green-600 rounded-md text-sm font-medium"
                >
                  Export CSV
                </button>
              </div>
              <div className="p-6">
                {/* Filters Row */}
                <div className="mb-4 flex flex-wrap gap-3 items-end">
                  {/* Month Dropdown */}
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Filter by Month</label>
                    <select
                      value={incomeMonthFilter}
                      onChange={(e) => {
                        setIncomeMonthFilter(e.target.value);
                        setIncomeFilterDate(''); // clear specific date when month is chosen
                      }}
                      className="w-full px-3 py-2 bg-gray-700 border-2 border-gray-600 rounded-md text-white focus:border-green-500 focus:outline-none"
                    >
                      <option value="">All Months</option>
                      {(() => {
                        const options = [];
                        const now = new Date();
                        for (let i = 0; i < 24; i++) {
                          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                          const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                          const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
                          options.push(<option key={value} value={value}>{label}</option>);
                        }
                        return options;
                      })()}
                    </select>
                  </div>

                  {/* Specific Date Picker */}
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-sm font-semibold text-gray-300 mb-1">Filter by Specific Date</label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        value={incomeFilterDate}
                        onChange={(e) => {
                          setIncomeFilterDate(e.target.value);
                          setIncomeMonthFilter(''); // clear month when specific date is chosen
                        }}
                        className="flex-1 px-3 py-2 bg-gray-700 border-2 border-gray-600 rounded-md text-white focus:border-green-500"
                      />
                      <button
                        onClick={() => {
                          setIncomeFilterDate('');
                          setIncomeMonthFilter('');
                        }}
                        className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md whitespace-nowrap"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                {/* Income Transactions Table */}
                <div className="overflow-y-auto max-h-96">
                  {isIncomeLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                    </div>
                  ) : (
                    <>
                      <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-900">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-300">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-300">First Name</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-300">Last Name</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-300">Plan Key</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-300">Add-ons</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-300">Total Amount</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-300">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {incomeTransactions.length === 0 ? (
                            <tr><td colSpan="7" className="px-3 py-6 text-center text-gray-400">No transactions found for this filter</td></tr>
                          ) : (
                            incomeTransactions.map((transaction) => {
                              const nameParts = (transaction.customer_name || '').split(' ');
                              const firstName = nameParts[0] || '-';
                              const lastName = nameParts.slice(1).join(' ') || '-';
                              const localDate = transaction.payment_date ? new Date(transaction.payment_date).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }) : '-';

                              return (
                                <tr key={transaction.transaction_id} className="hover:bg-gray-700">
                                  <td className="px-3 py-2 text-sm text-white">{localDate}</td>
                                  <td className="px-3 py-2 text-sm text-white">{firstName}</td>
                                  <td className="px-3 py-2 text-sm text-white">{lastName}</td>
                                  <td className="px-3 py-2 text-sm text-gray-300">{transaction.plan_key || '-'}</td>
                                  <td className="px-3 py-2 text-sm text-gray-300">{transaction.add_ons || '-'}</td>
                                  <td className="px-3 py-2 text-sm text-right font-semibold text-green-400">${transaction.total_amount}</td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      onClick={() => handleDeleteIncome(transaction.transaction_id)}
                                      className="text-red-400 hover:text-red-300 text-sm font-medium"
                                      title="Delete this income entry"
                                    >
                                      🗑️ Delete
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Personal Expenses Tab */}
      {
        activeTab === 'expenses' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            {/* Summary Cards */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">Personal Expenses Summary</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setPersonalExpensesRange('week');
                      setPersonalExpenseMonthFilter('');
                      loadPersonalExpenses(daysAgoToronto(7), todayToronto());
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${personalExpensesRange === 'week'
                      ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    This Week
                  </button>
                  <button
                    onClick={() => {
                      setPersonalExpensesRange('month');
                      setPersonalExpenseMonthFilter('');
                      loadPersonalExpenses(startOfCurrentMonthToronto(), todayToronto());
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${personalExpensesRange === 'month'
                      ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => {
                      setPersonalExpensesRange('all');
                      setPersonalExpenseMonthFilter('');
                      loadPersonalExpenses();
                    }}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${personalExpensesRange === 'all'
                      ? 'bg-gray-500 text-white ring-2 ring-gray-300'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    All
                  </button>
                  {/* Month Dropdown */}
                  <select
                    value={personalExpenseMonthFilter}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        setPersonalExpensesRange('custom_month');
                        setPersonalExpenseMonthFilter(val);
                        const [year, month] = val.split('-');
                        const lastDay = new Date(year, month, 0).getDate();
                        loadPersonalExpenses(`${val}-01`, `${val}-${lastDay}`);
                      } else {
                        setPersonalExpensesRange('all');
                        setPersonalExpenseMonthFilter('');
                        loadPersonalExpenses();
                      }
                    }}
                    className={`px-3 py-1 bg-gray-700 border border-gray-600 rounded-md text-sm font-medium focus:border-purple-500 focus:outline-none transition-colors ${personalExpensesRange === 'custom_month'
                      ? 'text-white border-purple-500 ring-2 ring-purple-400'
                      : 'text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    <option value="">Select Month</option>
                    {(() => {
                      const options = [];
                      const now = new Date();
                      for (let i = 0; i < 24; i++) {
                        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        const label = d.toLocaleString('default', { month: 'short', year: 'numeric' }); // Jan, Feb, etc.
                        options.push(<option key={value} value={value}>{label}</option>);
                      }
                      return options;
                    })()}
                  </select>
                </div>
              </div>

              {/* Total and Breakdown by Reason */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {/* Total Personal Expenses Card */}
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6 shadow-lg">
                  <p className="text-purple-100 text-sm mb-1">Total Personal Expenses</p>
                  <p className="text-4xl font-bold text-white">
                    ${personalExpensesTotalAmount.toFixed(2)}
                  </p>
                  <p className="text-purple-200 text-xs mt-2">{personalExpenses.length} transactions shown</p>
                </div>

                {/* Breakdown by Reason */}
                <div className="bg-gray-800 rounded-lg p-6 border-2 border-purple-500">
                  <h3 className="text-lg font-bold text-white mb-3">Breakdown by Reason</h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {expenseBreakdownByReason.map(([reason, data]) => (
                      <div key={reason} className="flex justify-between items-center">
                        <span className="text-gray-300 text-sm">{reason} ({data.count})</span>
                        <span className="text-purple-400 font-semibold text-sm">${data.total.toFixed(2)}</span>
                      </div>
                    ))}
                    {personalExpenses.length === 0 && (
                      <p className="text-gray-400 text-sm">No expenses recorded</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-white mb-4">Add & Manage Personal Expenses</h2>

            {/* Add Expense Form - Inline */}
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-orange-500 mb-6">
              <h3 className="text-lg font-bold text-white mb-4">Add Personal Expense</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Expense Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Expense Amount ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="0.00"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Date *</label>
                  <input
                    type="date"
                    value={newExpense.expense_date}
                    onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                {/* Reason */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-300">Reason *</label>
                    <button
                      onClick={() => setShowManageReasonsModal(true)}
                      className="text-xs text-purple-400 hover:text-purple-300"
                    >
                      Manage
                    </button>
                  </div>
                  {!showNewReasonInput ? (
                    <div className="flex gap-2">
                      <select
                        value={newExpense.reason}
                        onChange={(e) => {
                          if (e.target.value === '__add_new__') {
                            setShowNewReasonInput(true);
                          } else {
                            setNewExpense({ ...newExpense, reason: e.target.value });
                          }
                        }}
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                      >
                        <option value="">Select Reason</option>
                        {expenseReasons.map((reason, idx) => (
                          <option key={idx} value={reason}>{reason}</option>
                        ))}
                        <option value="__add_new__">+ Add New Reason</option>
                      </select>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newReasonInput}
                        onChange={(e) => setNewReasonInput(e.target.value)}
                        placeholder="Enter new reason..."
                        className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                      />
                      <button
                        onClick={addExpenseReason}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
                        title="Save new reason"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => {
                          setShowNewReasonInput(false);
                          setNewReasonInput('');
                        }}
                        className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Payment Method (optional)</label>
                  <input
                    type="text"
                    value={newExpense.payment_method}
                    onChange={(e) => setNewExpense({ ...newExpense, payment_method: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="E-Transfer, Cash, etc."
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={newExpense.description}
                    onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="Optional description..."
                  />
                </div>

                {/* Add Button */}
                <div className="flex items-end">
                  <button
                    onClick={addPersonalExpense}
                    disabled={submitting}
                    className={`w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md flex items-center justify-center space-x-2 ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Plus size={18} />
                    <span>Add Expense</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Personal Expenses Table */}
            <div className="bg-gray-800 rounded-lg shadow-xl border-2 border-purple-500 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Payment Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {personalExpensesLoading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8">
                        <div className="flex justify-center items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                        </div>
                      </td>
                    </tr>
                  ) : personalExpenses.length > 0 ? (
                    personalExpenses.map((expense) => {
                      const localDate = expense.expense_date ? new Date(expense.expense_date).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }) : '-';
                      return (
                        <tr key={expense.expense_id} className="hover:bg-gray-700">
                          <td className="px-6 py-4 text-sm text-white">{localDate}</td>
                          <td className="px-6 py-4 text-sm text-white">{expense.reason}</td>
                          <td className="px-6 py-4 text-sm text-white font-bold">{formatCurrency(expense.amount)}</td>
                          <td className="px-6 py-4 text-sm text-white">{expense.payment_method || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-300">{expense.description || '-'}</td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => deletePersonalExpense(expense._id)}
                              className="text-red-500 hover:text-red-400"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-400">No personal expenses recorded</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      }


      {/* Customers Tab */}
      {
        activeTab === 'customers' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <CustomersTab setMessage={setMessage} setError={setError} />
          </div>
        )
      }

      {/* Operations Tab with Sub-navigation */}
      {
        activeTab === 'operations' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-orange-500 mb-6">
              <div className="flex flex-wrap gap-4 border-b border-gray-700 pb-4 mb-6">
                <button
                  onClick={() => setActiveOperationsTab('routes')}
                  className={`px-4 py-2 font-medium rounded-md transition-colors flex items-center gap-2 ${activeOperationsTab === 'routes'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                >
                  <Truck size={18} />
                  Routes & Dispatching
                </button>
                <button
                  onClick={() => setActiveOperationsTab('drivers')}
                  className={`px-4 py-2 font-medium rounded-md transition-colors flex items-center gap-2 ${activeOperationsTab === 'drivers'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                >
                  <UsersIcon size={18} />
                  Driver Management
                </button>
                <button
                  onClick={() => setActiveOperationsTab('kitchen')}
                  className={`px-4 py-2 font-medium rounded-md transition-colors flex items-center gap-2 ${activeOperationsTab === 'kitchen'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                >
                  <Utensils size={18} />
                  Kitchen Menu
                </button>
              </div>

              {/* Sub-tab Content */}
              {activeOperationsTab === 'routes' && (
                <DispatchingTab setMessage={setMessage} setError={setError} />
              )}
              {activeOperationsTab === 'drivers' && (
                <DriverManagementTab setMessage={setMessage} setError={setError} />
              )}
              {activeOperationsTab === 'kitchen' && (
                <WeeklyMenuTab setMessage={setMessage} setError={setError} />
              )}
            </div>
          </div>
        )
      }

      {/* Renewals Tab */}
      {
        activeTab === 'renewals' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <div className="bg-gray-800 rounded-lg p-6 shadow-xl border-2 border-orange-500">
              <RenewalsTab setMessage={setMessage} setError={setError} />
            </div>
          </div>
        )
      }

      {/* Staff Login Tab replacing the old Settings */}
      {
        activeTab === 'staff-login' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <div className="mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-2 border-orange-500 bg-gray-800 p-4 rounded-lg shadow-xl">
              <h2 className="text-2xl font-bold text-white">Staff Time Logs</h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-300">Filter by Date:</label>
                  <input
                    type="date"
                    value={logFilterDate}
                    onChange={(e) => setLogFilterDate(e.target.value)}
                    className="border border-gray-600 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
                {/* 
                  Hardcoded to kitchen filter as requested by user.
                  If the user ever wants other roles, we can swap this back to a select dropdown.
                */}
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded text-sm font-medium bg-orange-500 text-white shadow-sm">
                    Filtering: Kitchen
                  </span>
                </div>
                <button
                  onClick={loadStaffLogs}
                  className="p-2 border border-gray-600 bg-gray-700 rounded-md hover:bg-gray-600 flex-shrink-0"
                  title="Refresh Time Logs"
                >
                  <RefreshCw size={16} className="text-gray-300" />
                </button>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-600">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Staff Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Clocked In</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Clocked Out</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Total Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {staffLogs?.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400 italic">
                          No staff logs found for the selected filters.
                        </td>
                      </tr>
                    ) : (
                      staffLogs?.map(log => {
                        const start = new Date(log.startTime);
                        const end = log.endTime ? new Date(log.endTime) : null;

                        // Calculate duration
                        let durationMsg = 'Active Shift';
                        if (end) {
                          const diffMs = end - start;
                          const hours = Math.floor(diffMs / 3600000);
                          const minutes = Math.floor((diffMs % 3600000) / 60000);
                          durationMsg = `${hours}h ${minutes}m`;
                        }

                        return (
                          <tr key={log._id} className="hover:bg-gray-700 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 font-medium">
                              {log.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="text-sm font-bold text-white">{log.staffId?.name || 'Unknown User'}</span>
                                <span className="text-xs text-gray-400">{log.staffId?.email}</span>
                                <div className="mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-500 text-white uppercase tracking-wide">
                                    {log.staffId?.role || 'Unknown Role'}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center text-sm font-medium text-green-400">
                                <Clock size={16} className="mr-1.5" />
                                {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {end ? (
                                <div className="flex items-center text-sm font-medium text-gray-400">
                                  <CheckCircle size={16} className="mr-1.5 text-gray-500" />
                                  {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900 text-red-100 animate-pulse border border-red-700">
                                  Working (No Stop Time)
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`text-sm font-bold ${end ? 'text-gray-300' : 'text-orange-400'}`}>
                                {durationMsg}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      }

      {/* User Management Tab */}
      {
        activeTab === 'users' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            {/* Header with Create User Button and Change Own Password */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-white">User Management</h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md"
                >
                  Change My Password
                </button>
                <button
                  onClick={handleCreateUser}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md flex items-center space-x-2"
                >
                  <Plus size={18} />
                  <span>Create User</span>
                </button>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800 rounded-lg shadow-xl border-2 border-orange-500 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.length > 0 ? (
                    users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{user.full_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'owner' ? 'bg-purple-600 text-white' :
                            user.role === 'admin' ? 'bg-blue-600 text-white' :
                              user.role === 'kitchen' ? 'bg-green-600 text-white' :
                                user.role === 'driver' ? 'bg-yellow-600 text-white' :
                                  'bg-gray-600 text-white'
                            }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${user.status === 'active' ? 'bg-green-600 text-white' :
                            user.status === 'disabled' ? 'bg-orange-600 text-white' :
                              user.status === 'deleted' ? 'bg-red-600 text-white' :
                                'bg-gray-600 text-white'
                            }`}>
                            {user.status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          {user.role !== 'owner' ? (
                            <>
                              <button
                                onClick={() => handleEditUser(user)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedUserForAction(user);
                                  setShowResetPasswordModal(true);
                                }}
                                className="text-yellow-400 hover:text-yellow-300"
                              >
                                Reset Password
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedUserForAction(user);
                                  setChangeRoleForm({ newRole: user.role, reason: '' });
                                  setShowChangeRoleModal(true);
                                }}
                                className="text-purple-400 hover:text-purple-300"
                              >
                                Change Role
                              </button>
                              {user.status !== 'disabled' && (
                                <button
                                  onClick={() => {
                                    setSelectedUserForAction(user);
                                    setShowDisableModal(true);
                                  }}
                                  className="text-orange-400 hover:text-orange-300"
                                >
                                  Disable
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setSelectedUserForAction(user);
                                  setShowDeleteModal(true);
                                }}
                                className="text-red-400 hover:text-red-300"
                              >
                                Delete
                              </button>
                            </>
                          ) : (
                            <span className="text-gray-500">Owner Account</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-gray-400">
                        No users found
                      </td>
                    </tr>
                  )}</tbody>
              </table>
            </div>
          </div >
        )
      }

      {/* Activity Logs Tab */}
      {
        activeTab === 'logs' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <ActivityLogTab setMessage={setMessage} setError={setError} endpoint="/owner/activity-logs" showActorFilter={true} />
          </div>
        )
      }


      {/* User Modal */}
      {
        showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full border-2 border-orange-500">
              <h3 className="text-2xl font-bold text-white mb-6">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="user@example.com"
                    disabled={!!editingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={userForm.full_name}
                    onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="John Doe"
                  />
                </div>

                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Role *</label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    >
                      <option value="admin">Admin</option>
                      <option value="kitchen">Kitchen</option>
                      <option value="driver">Delivery Driver</option>
                      <option value="packaging">Packaging</option>
                    </select>
                  </div>
                )}

                {/* Driver Specific: Assign Route */}
                {userForm.role === 'driver' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Assign Route (Optional)</label>
                    <select
                      value={userForm.assigned_route_id || ''}
                      onChange={(e) => setUserForm({ ...userForm, assigned_route_id: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    >
                      <option value="">No Route Assigned</option>
                      {routes.map(route => (
                        <option key={route.id} value={route.id}>
                          {route.name} ({route.area})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Password {editingUser && '(leave blank to keep current)'}
                  </label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder={editingUser ? "Leave blank to keep current" : "Min 8 chars, mixed case"}
                  />
                </div>
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={handleSaveUser}
                  disabled={submitting}
                  className={`flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {editingUser ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setError('');
                    setEditingUser(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Change Own Password Modal */}
      {
        showChangePasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full border-2 border-purple-500">
              <h3 className="text-2xl font-bold text-white mb-6">Change Your Password</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.old_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, old_password: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  />
                </div>
              </div>

              <div className="flex space-x-4 mt-6">
                <button
                  onClick={handleChangeOwnPassword}
                  disabled={submitting}
                  className={`flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Change Password
                </button>
                <button
                  onClick={() => setShowChangePasswordModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }
      {console.log("editingPricing", editingPricing)}

      {/* Change Pricing Tab */}
      {
        activeTab === 'pricing' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <div className="bg-gray-800 rounded-lg p-8 shadow-xl border-2 border-orange-500">
              <h2 className="text-2xl font-bold text-white mb-4">Change Pricing</h2>
              <div className="bg-yellow-900 border border-yellow-700 text-yellow-200 px-4 py-3 rounded mb-6">
                <p className="font-medium">⚠️ Important:</p>
                <ul className="list-disc ml-5 mt-2 space-y-1">
                  <li>New prices will apply to all new customers and renewals</li>
                  <li>Existing customers keep their current pricing until renewal</li>
                  <li>Changes will update across all dashboards and signup forms</li>
                </ul>
              </div>

              {/* Meal Plans Pricing */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-4">Meal Plans Pricing</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
                    <thead className="bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Meal Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Term</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Billing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Current Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">New Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {Object.entries(editingPricing.plans || {}).map(([mealType, terms]) => (
                        Object.entries(terms).map(([mealTerm, billingTerms]) => (
                          Object.entries(billingTerms).map(([billingTerm, price]) => (
                            <tr key={`${mealType}-${mealTerm}-${billingTerm}`} className="hover:bg-gray-600">
                              <td className="px-6 py-4 text-sm text-white">{mealType}</td>
                              <td className="px-6 py-4 text-sm text-white">{mealTerm}</td>
                              <td className="px-6 py-4 text-sm text-white">{billingTerm}</td>
                              <td className="px-6 py-4 text-sm text-gray-300">${price}</td>
                              <td className="px-6 py-4">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={price}
                                  onChange={(e) => updatePlanPrice(mealType, mealTerm, billingTerm, e.target.value)}
                                  className="w-32 px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white"
                                />
                              </td>
                            </tr>
                          ))
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add-ons Pricing - MOVED FIRST */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-4">Add-ons Pricing</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
                    <thead className="bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Add-on Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Billing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Current Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">New Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {Object.entries(editingPricing.addons || {}).map(([addonName, billingTerms]) => (
                        Object.entries(billingTerms || {}).map(([billingTerm, price]) => (
                          <tr key={`${addonName}-${billingTerm}`} className="hover:bg-gray-600">
                            <td className="px-6 py-4 text-sm text-white">{addonName}</td>
                            <td className="px-6 py-4 text-sm text-white">{billingTerm}</td>
                            <td className="px-6 py-4 text-sm text-gray-300">${price}</td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                step="0.01"
                                value={price}
                                onChange={(e) => updateAddonPrice(addonName, billingTerm, e.target.value)}
                                className="w-32 px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white"
                              />
                            </td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Truckers Plans Pricing - MOVED SECOND */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-white mb-4">Truckers Plans Pricing</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
                    <thead className="bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Trucker Plan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Plan Term</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Current Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">New Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-600">
                      {Object.entries(editingPricing.truckers || {}).map(([truckerType, terms]) => (
                        Object.entries(terms || {}).map(([truckerTerm, price]) => (
                          <tr key={`${truckerType}-${truckerTerm}`} className="hover:bg-gray-600">
                            <td className="px-6 py-4 text-sm text-white">{truckerType}</td>
                            <td className="px-6 py-4 text-sm text-white">{truckerTerm}</td>
                            <td className="px-6 py-4 text-sm text-gray-300">${price}</td>
                            <td className="px-6 py-4">
                              <input
                                type="number"
                                step="0.01"
                                value={price}
                                onChange={(e) => updateTruckerPrice(truckerType, truckerTerm, e.target.value)}
                                className="w-32 px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white"
                              />
                            </td>
                          </tr>
                        ))
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={updatePricing}
                  disabled={submitting}
                  className={`px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-medium ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Update Pricing
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Duplicate ActivityLogTab removed — see L2381 for the canonical rendering */}

      {/* Payments Tab */}
      {
        activeTab === 'payments' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <PaymentHistoryTab setMessage={setMessage} setError={setError} />
          </div>
        )
      }

      {/* Feedback Tab */}
      {
        activeTab === 'feedback' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <FeedbackTab setMessage={setMessage} setError={setError} />
          </div>
        )
      }

      {
        activeTab === 'cancellations' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <CancellationRequestsTab apiBase="/owner" setMessage={setMessage} setError={setError} onCountChange={loadCancellationCount} />
          </div>
        )
      }

      {/* Customer Passwords Tab */}
      {
        activeTab === 'customer-passwords' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
            <CustomerPasswordsTab setMessage={setMessage} setError={setError} />
          </div>
        )
      }

      {/* Phase 11: Reset Password Modal */}
      {
        showResetPasswordModal && selectedUserForAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-orange-500">
              <h3 className="text-xl font-bold text-white mb-4">Reset Password for {selectedUserForAction.email}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">New Password</label>
                  <input
                    type="password"
                    value={resetPasswordForm.newPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    placeholder="Confirm new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reason (min 10 characters) *</label>
                  <textarea
                    value={resetPasswordForm.reason}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, reason: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    rows="3"
                    placeholder="Explain why you are resetting this password..."
                  />
                  <p className="text-xs text-gray-400 mt-1">{resetPasswordForm.reason.length}/10 characters</p>
                </div>
                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-sm font-semibold text-orange-400 mb-1">Confirm Your Password (Re-authenticate)</label>
                  <input
                    type="password"
                    value={resetPasswordForm.reauthPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, reauthPassword: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-orange-500 rounded-md text-white"
                    placeholder="Enter your owner password to confirm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setResetPasswordForm({ newPassword: '', confirmPassword: '', reason: '', reauthPassword: '' });
                    setSelectedUserForAction(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={submitting}
                  className={`px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Phase 11: Change Role Modal */}
      {
        showChangeRoleModal && selectedUserForAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-orange-500">
              <h3 className="text-xl font-bold text-white mb-4">Change Role for {selectedUserForAction.email}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Current Role</label>
                  <input
                    type="text"
                    value={selectedUserForAction.role}
                    disabled
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-600 rounded-md text-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">New Role *</label>
                  <select
                    value={changeRoleForm.newRole}
                    onChange={(e) => setChangeRoleForm({ ...changeRoleForm, newRole: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                  >
                    <option value="">Select new role...</option>
                    <option value="admin">Admin</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="driver">Driver</option>
                    <option value="packaging">Packaging</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reason (min 10 characters) *</label>
                  <textarea
                    value={changeRoleForm.reason}
                    onChange={(e) => setChangeRoleForm({ ...changeRoleForm, reason: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    rows="3"
                    placeholder="Explain why you are changing this user's role..."
                  />
                  <p className="text-xs text-gray-400 mt-1">{changeRoleForm.reason.length}/10 characters</p>
                </div>
                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-sm font-semibold text-purple-400 mb-1">Confirm Your Password (Re-authenticate)</label>
                  <input
                    type="password"
                    value={changeRoleForm.reauthPassword}
                    onChange={(e) => setChangeRoleForm({ ...changeRoleForm, reauthPassword: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-purple-500 rounded-md text-white"
                    placeholder="Enter your owner password to confirm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowChangeRoleModal(false);
                    setChangeRoleForm({ newRole: '', reason: '', reauthPassword: '' });
                    setSelectedUserForAction(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangeRole}
                  className={`px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-md ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Change Role
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Phase 11: Delete User Modal */}
      {
        showDeleteModal && selectedUserForAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-red-500">
              <h3 className="text-xl font-bold text-red-500 mb-4">Delete User: {selectedUserForAction.email}</h3>
              <p className="text-gray-300 mb-4">This action will permanently delete this user account. This cannot be undone.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reason (min 10 characters) *</label>
                  <textarea
                    value={deleteDisableForm.reason}
                    onChange={(e) => setDeleteDisableForm({ ...deleteDisableForm, reason: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    rows="3"
                    placeholder="Explain why you are deleting this user..."
                  />
                  <p className="text-xs text-gray-400 mt-1">{deleteDisableForm.reason.length}/10 characters</p>
                </div>
                <div>
                  <label className="flex items-center text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={deleteDisableForm.confirmAction}
                      onChange={(e) => setDeleteDisableForm({ ...deleteDisableForm, confirmAction: e.target.checked })}
                      className="mr-2"
                    />
                    I understand this action is irreversible
                  </label>
                </div>
                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-sm font-semibold text-red-400 mb-1">Confirm Your Password (Re-authenticate)</label>
                  <input
                    type="password"
                    value={deleteDisableForm.reauthPassword}
                    onChange={(e) => setDeleteDisableForm({ ...deleteDisableForm, reauthPassword: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-red-500 rounded-md text-white"
                    placeholder="Enter your owner password to confirm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteDisableForm({ reason: '', confirmAction: false, reauthPassword: '' });
                    setSelectedUserForAction(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={submitting}
                  className={`px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Delete User
                </button>
              </div>
            </div>
          </div >
        )
      }

      {/* Phase 11: Disable User Modal */}
      {
        showDisableModal && selectedUserForAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-orange-500">
              <h3 className="text-xl font-bold text-orange-500 mb-4">Disable User: {selectedUserForAction.email}</h3>
              <p className="text-gray-300 mb-4">This will revoke access for this user. They will not be able to login.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Reason (min 10 characters) *</label>
                  <textarea
                    value={deleteDisableForm.reason}
                    onChange={(e) => setDeleteDisableForm({ ...deleteDisableForm, reason: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                    rows="3"
                    placeholder="Explain why you are disabling this user..."
                  />
                  <p className="text-xs text-gray-400 mt-1">{deleteDisableForm.reason.length}/10 characters</p>
                </div>
                <div>
                  <label className="flex items-center text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={deleteDisableForm.confirmAction}
                      onChange={(e) => setDeleteDisableForm({ ...deleteDisableForm, confirmAction: e.target.checked })}
                      className="mr-2"
                    />
                    I understand this action is irreversible
                  </label>
                </div>
                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-sm font-semibold text-orange-400 mb-1">Confirm Your Password (Re-authenticate)</label>
                  <input
                    type="password"
                    value={deleteDisableForm.reauthPassword}
                    onChange={(e) => setDeleteDisableForm({ ...deleteDisableForm, reauthPassword: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-orange-500 rounded-md text-white"
                    placeholder="Enter your owner password to confirm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowDisableModal(false);
                    setDeleteDisableForm({ reason: '', confirmAction: false, reauthPassword: '' });
                    setSelectedUserForAction(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisableUser}
                  disabled={submitting}
                  className={`px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Disable User
                </button>
              </div>
            </div >
          </div >
        )
      }

      {/* Manage Expense Reasons Modal */}
      {
        showManageReasonsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border-2 border-purple-500">
              <h3 className="text-xl font-bold text-white mb-4 border-b border-gray-700 pb-2">
                Manage Personal Expense Reasons
              </h3>

              <div className="max-h-64 overflow-y-auto pr-2 mb-4 space-y-2">
                {expenseReasons.length > 0 ? (
                  expenseReasons.map((reason, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-gray-700 p-3 rounded shadow">
                      <span className="text-white text-sm">{reason}</span>
                      <button
                        onClick={() => deleteExpenseReasonHandler(reason)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete Reason"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm italic text-center py-4">No custom reasons available.</p>
                )}
              </div>

              {/* Bottom Actions */}
              <div className="flex justify-end pt-4 border-t border-gray-700">
                <button
                  onClick={() => setShowManageReasonsModal(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

    </div >

  );
};

export default OwnerDashboardNew;
